import { useMemo } from 'react';
import { cn } from '@/app/lib/utils';
import { TagButton } from './TagButton';

export interface TagFilterItem {
  id: string;
  label: string;
  isSelected: boolean;
  isDisabled?: boolean;
}

interface TagFilterProps {
  tags?: TagFilterItem[];
  onTagClick?: (tagId: string) => void;
  onChangeSelected?: (selectedIds: string[]) => void;
  multiSelect?: boolean;
  className?: string;
  classNames?: {
    button?: string;
    selectedButton?: string;
    disabledButton?: string;
  };
}

const ensureAllTag = (tags: TagFilterItem[] | undefined): TagFilterItem[] => {
  const list = tags?.length ? [...tags] : [];
  const hasAll = list.some((t) => t.id === 'all');
  if (!hasAll)
    list.unshift({
      id: 'all',
      label: 'All',
      isSelected: list.every((t) => !t.isSelected) || !list.length,
    });
  return list;
};

export function TagFilter({
  tags,
  onTagClick,
  onChangeSelected,
  multiSelect = false,
  className = '',
  classNames,
}: TagFilterProps) {
  const safeTags = useMemo(() => ensureAllTag(tags), [tags]);

  const computeNext = (clickedId: string): string[] => {
    const current = new Set(safeTags.filter((t) => t.isSelected).map((t) => t.id));

    if (!multiSelect) {
      return [clickedId];
    }

    if (clickedId === 'all') return ['all'];

    if (current.has(clickedId)) current.delete(clickedId);
    else current.add(clickedId);

    current.delete('all');
    if (current.size === 0) return ['all'];
    return Array.from(current);
  };

  const handleTagClick = (tagId: string) => {
    const next = computeNext(tagId);
    if (multiSelect) {
      onChangeSelected?.(next);
    } else {
      onTagClick?.(next[0]);
      onChangeSelected?.(next);
    }
  };

  return (
    <div className={cn('flex flex-wrap gap-xs', className)}>
      {safeTags.map((tag, index) => (
        <TagButton
          key={`${tag.id}-${index}`}
          label={tag.label}
          isSelected={tag.isSelected}
          isDisabled={tag.isDisabled}
          onClick={() => !tag.isDisabled && handleTagClick(tag.id)}
          classNames={classNames}
        />
      ))}
    </div>
  );
}
